import { useState, type ReactNode, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';

export const NAME_MAX = 40;

/** Shows `n/max` or `n` only while the field is focused and not empty. */
export function CountedField({
  value,
  onChange,
  limit,
  multiline,
  rows = 3,
  className = 'input',
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  limit?: number;
  multiline?: boolean;
  rows?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'>) {
  const [focus, setFocus] = useState(false);
  const count = value.length;
  const show = focus && count > 0;
  const over = limit != null && count > limit;
  const counter = <span className={`pointer-events-none absolute bottom-1 right-2 text-[10px] ${over ? 'text-rose-300' : 'text-zinc-500'}`}>{limit != null ? `${count}/${limit}` : String(count)}</span>;
  return (
    <div className="relative">
      {multiline ? (
        <textarea className={`${className} ${show ? 'pb-4' : ''}`} rows={rows} value={value} maxLength={limit} onChange={(e) => onChange(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)} />
      ) : (
        <input className={className} value={value} maxLength={limit} onChange={(e) => onChange(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} {...(rest as InputHTMLAttributes<HTMLInputElement>)} />
      )}
      {show && counter}
    </div>
  );
}

export function Section({ title, children, actions }: { title: ReactNode; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="card space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-zinc-200">{title}</h2>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export function Message({ text, kind = 'info' }: { text: string | null; kind?: 'info' | 'error' | 'ok' }) {
  if (!text) return null;
  const color = kind === 'error' ? 'text-rose-300 border-rose-900/60 bg-rose-950/30' : kind === 'ok' ? 'text-emerald-300 border-emerald-900/60 bg-emerald-950/30' : 'text-zinc-300 border-zinc-800 bg-zinc-900';
  return <div className={`rounded-lg border px-3 py-2 text-xs font-mono break-all ${color}`}>{text}</div>;
}

export function Json({ value, className = '' }: { value: unknown; className?: string }) {
  return <pre className={`json ${className}`}>{JSON.stringify(value, null, 2)}</pre>;
}

/** Textarea-backed JSON editor with parse feedback. */
export function JsonEditor({ value, onChange, rows = 18, error }: { value: string; onChange: (text: string) => void; rows?: number; error?: string | null }) {
  return (
    <div className="space-y-1">
      <textarea className="input font-mono text-xs leading-5" rows={rows} value={value} onChange={(e) => onChange(e.target.value)} spellCheck={false} />
      {error && <div className="text-xs text-rose-300">{error}</div>}
    </div>
  );
}

export function useJsonText(initial: unknown) {
  const [text, setText] = useState(() => JSON.stringify(initial, null, 2));
  const [error, setError] = useState<string | null>(null);
  const parse = (): unknown | undefined => {
    try {
      const v = JSON.parse(text) as unknown;
      setError(null);
      return v;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return undefined;
    }
  };
  const reset = (value: unknown) => {
    setText(JSON.stringify(value, null, 2));
    setError(null);
  };
  return { text, setText, error, parse, reset };
}

export function Pill({ children, tone = 'zinc' }: { children: ReactNode; tone?: 'zinc' | 'indigo' | 'emerald' | 'rose' | 'amber' }) {
  const tones = { zinc: 'bg-zinc-800 text-zinc-300', indigo: 'bg-indigo-900/60 text-indigo-200', emerald: 'bg-emerald-900/60 text-emerald-200', rose: 'bg-rose-900/60 text-rose-200', amber: 'bg-amber-900/60 text-amber-200' };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] ${tones[tone]}`}>{children}</span>;
}

export function FileButton({ label, accept, onFile, disabled }: { label: string; accept?: string; onFile: (file: File) => void; disabled?: boolean }) {
  return (
    <label className={`btn-ghost cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
      {label}
      <input
        type="file"
        className="hidden"
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
    </label>
  );
}

export const fmtBytes = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`);
export const fmtTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');
